import React from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import FileItem from "fe-shared/src/components/FileList/FileItem";
import { downloadFile } from "fe-shared/src/helpers/files";

import { priceToFormat } from "../../helpers/invoice";

import {
  InvoiceExternalAdjustment,
  InvoiceInternalAdjustment,
  InvoiceAdjustmentReason,
} from "../../types";

import styles from "./InvoiceAdjustmentsDetails.module.scss";
import useTenantOptions from "fe-shared/src/hooks/useTenantOptions";

interface Props {
  externalAdjustment?: InvoiceExternalAdjustment;
  internalAdjustments?: InvoiceInternalAdjustment[];
  adjustmentSum: number;
}

const renderAdjustmentSum = (sum: number) => (
  <span
    className={sum < 0 ? styles.invoiceAdjustmentDetailsSumWrapperAlert : ""}
  >
    {priceToFormat(sum, true)}
  </span>
);

const getReasons = (reasons: InvoiceAdjustmentReason[]) =>
  reasons.map((item) => item.adjustmentReason).join(", ");

const InvoiceAdjustmentsDetails = (props: Props) => {
  const { t } = useTranslation();
  const [ tenantOptions ] = useTenantOptions();
  const { externalAdjustment, internalAdjustments = [], adjustmentSum } = props;

  return (
    <div className={styles.invoiceAdjustmentDetails}>
      <h4>{t("invoices.adjustments.title")}</h4>
      <div className={styles.invoiceAdjustmentDetailsHeader}>
        <span>{t("invoices.adjustments.reasons")}</span>
        <span>{t("invoices.adjustments.amount")}</span>
      </div>
      {!!externalAdjustment && (
        <div className={styles.invoiceAdjustmentDetailsItem}>
          <div className={styles.invoiceAdjustmentDetailsMember}>
            <span>{t("invoices.adjustments.byParticipant")}</span>
          </div>
          <div className={styles.invoiceAdjustmentDetailsSumWrapper}>
            <span>{externalAdjustment.comment}</span>
            {renderAdjustmentSum(externalAdjustment.amount)}
          </div>
        </div>
      )}
      {!!internalAdjustments.length &&
        internalAdjustments.map((item) => (
          <div
            key={item.adjustmentId}
            className={classnames(styles.invoiceAdjustmentDetailsItem, {
              [styles.paddingBottom]: item.attachments?.length,
            })}
          >
            <div className={styles.invoiceAdjustmentDetailsMember}>
              <span>{t("invoices.adjustments.byOpCompany", {tenantName: tenantOptions.tenantName})}</span>
            </div>
            <div className={styles.invoiceAdjustmentDetailsSumWrapper}>
              <span>{getReasons(item.reasons)}</span>
              {renderAdjustmentSum(item.amount)}
            </div>
            <div className={styles.invoiceAdjustmentDetailsComment}>
              <span>{item.comment}</span>
            </div>
            {item.attachments?.map(({ documentPath, ...rest }) => (
              <FileItem
                disabled
                isViewOnly
                isDownloadActive
                id={documentPath}
                key={documentPath}
                onDownload={(id, fileName) => {
                  downloadFile(documentPath, fileName);
                }}
                {...rest}
              />
            ))}
          </div>
        ))}
      <div className={styles.invoiceAdjustmentDetailsTotal}>
        <span>{t("invoices.adjustmentTotal")}</span>
        {renderAdjustmentSum(adjustmentSum)}
      </div>
    </div>
  );
};

export default InvoiceAdjustmentsDetails;
